import { useCallback, useState, useEffect, useRef } from 'react';
import { Link } from '@mui/material';
import { MoreActionsHorizontal, MoreActionsVertical } from '@sweep-io/sweep-design/dist/icons';
import Menu from '@mui/material/Menu';
import RestrictedTooltip from '../../../common/permissions/RestrictedTooltip';
import WithConnectOrgPermission from '../../../common/WithConnectOrgPermission';
import WithPermissions from '../../../common/WithPermissions';
import { MenuItem } from '@mui/material';
import { Typography, IconButton } from '@sweep-io/sweep-design';
import { colors } from '@sweep-io/sweep-design';
import { CrmOrg } from '../../../../types/CrmOrgs';
import { EditableLabelRef } from '../../../common/EditableLabel';
import { useSelector } from 'react-redux';
import { UNAUTHORIZED_FETCH_TOOLTIP } from '../../../../constants';
import { useFeatureToggle } from '../../../../feature-flags/useFeatureToggle';
import { useIsSetupCompleted } from '../../../../hooks/useIsSetupCompleted';
import useUserInfo from '../../../../hooks/useUserInfo';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { selectUserMcpOrgId } from '../../../../reducers/userInfoReducer';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { getCrmOrgConnectionErrorData } from '../../get-started/connect-org/getCrmOrgConnectionErrorData';
import { EnvironmentTypes } from '../EnvironmentTypeEnum';
import { useCrmOrgs } from '../useCrmOrgs';

interface EnvironmentActionButtonsProps {
  onCloseMenu?: () => void;
  editableLabelRef: React.RefObject<EditableLabelRef> | (() => EditableLabelRef | undefined);
  env: CrmOrg;
  allowDelete: boolean;
  setDeleteDialogOpenEnv: (env: CrmOrg) => void;
  setCompleteSetupDialogOpenEnv: (env: CrmOrg) => void;
  setFactoryResetDialogOpenEnv: (env: CrmOrg) => void;
}

export const EnvironmentActionButtons = ({
  onCloseMenu,
  editableLabelRef,
  env,
  allowDelete,
  setDeleteDialogOpenEnv,
  setCompleteSetupDialogOpenEnv,
  setFactoryResetDialogOpenEnv,
}: EnvironmentActionButtonsProps) => {
  const { orgFactoryReset, setAsMcp, environmentsPage } = useFeatureToggle();
  const [menuTarget, setMenuTarget] = useState<null | HTMLElement>(null);
  const { addNotification } = useSweepNotifications();
  const userMcpOrgId = useSelector(selectUserMcpOrgId);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const isSetupCompleted = useIsSetupCompleted(env.id);

  const { connectOrg } = useCrmOrgs();
  const { handleSetUserMcpOrgId } = useUserInfo();

  // Find the parent table row and update its data attribute directly
  useEffect(() => {
    if (!buttonRef.current) return;

    const tableRow = buttonRef.current.closest('tr');
    if (!tableRow) return;

    if (menuTarget) {
      tableRow.setAttribute('data-menu-open', 'true');
    } else {
      tableRow.removeAttribute('data-menu-open');
    }
  }, [menuTarget]);

  const closeMenu = useCallback(() => {
    setMenuTarget(null);
    onCloseMenu?.();
  }, [onCloseMenu]);

  const setMcpOrgHandler = useCallback(async () => {
    await handleSetUserMcpOrgId(env.id);
    closeMenu();
  }, [handleSetUserMcpOrgId, env.id, closeMenu]);

  const reconnectHandler = async () => {
    const result = await connectOrg({
      crmOrgToReconnect: env,
      type: env.isSandbox ? EnvironmentTypes.Sandbox : EnvironmentTypes.Production,
    });
    if (result.result === 'error') {
      const { description, title } = getCrmOrgConnectionErrorData({
        error: result.error,
      });
      addNotification({
        message: title,
        details: description.join(' '),
        variant: SweepNotificationVariant.Error,
      });
    }
  };

  return (
    <>
      <IconButton
        ref={buttonRef}
        size={environmentsPage ? 'small' : 'tiny'}
        variant={environmentsPage ? 'system' : 'flat'}
        onClick={(ev) => setMenuTarget(ev.currentTarget)}
      >
        {environmentsPage ? <MoreActionsHorizontal /> : <MoreActionsVertical />}
      </IconButton>

      <Menu anchorEl={menuTarget} open={!!menuTarget} onClose={closeMenu} disableRestoreFocus>
        <RestrictedTooltip
          to={['edit:orgs']}
          notAllowedTitle="To edit this environment's name, please contact your admin."
          component={(isAllowed) => (
            <MenuItem
              onClick={() => {
                const ref =
                  typeof editableLabelRef === 'function'
                    ? editableLabelRef()
                    : editableLabelRef.current;
                ref?.edit();
                closeMenu();
              }}
              disabled={!isAllowed}
            >
              Rename
            </MenuItem>
          )}
        />

        <WithConnectOrgPermission>
          <MenuItem
            onClick={() => {
              reconnectHandler();
              closeMenu();
            }}
          >
            Reconnect
          </MenuItem>
        </WithConnectOrgPermission>

        {setAsMcp && userMcpOrgId !== env.id && (
          <WithPermissions
            permissionStr={['edit:users:mcp-org']}
            disabledTooltip="To set this environment as MCP, please contact your admin."
          >
            <MenuItem onClick={setMcpOrgHandler}>Set as MCP</MenuItem>
          </WithPermissions>
        )}

        {orgFactoryReset && (
          <WithPermissions
            permissionStr={['create:org:fetch-job']}
            disabledTooltip={UNAUTHORIZED_FETCH_TOOLTIP}
          >
            <MenuItem
              onClick={() => {
                setFactoryResetDialogOpenEnv?.(env);
                closeMenu();
              }}
            >
              Factory Reset
            </MenuItem>
          </WithPermissions>
        )}

        {!isSetupCompleted && (
          <MenuItem
            onClick={() => {
              setCompleteSetupDialogOpenEnv?.(env);
              closeMenu();
            }}
          >
            Complete Setup
          </MenuItem>
        )}

        <MenuItem>
          <Link href={env.instanceUrl} target="_blank" underline="none">
            <Typography variant="body" color={colors.blue[500]}>
              Go to Environment
            </Typography>
          </Link>
        </MenuItem>
        <RestrictedTooltip
          to={['delete:orgs']}
          notAllowedTitle="To delete this environment, please contact your admin."
          component={(isAllowed) => (
            <MenuItem
              disabled={!allowDelete || !isAllowed}
              onClick={() => {
                setDeleteDialogOpenEnv?.(env);
                closeMenu();
              }}
            >
              <Typography variant="body" color={colors.blush[500]}>
                Remove Environment
              </Typography>
            </MenuItem>
          )}
        />
      </Menu>
    </>
  );
};
